import React, { MouseEvent, useCallback, useEffect, useState } from "react";
import { Collapse, Form, Modal, Row, Spin } from "antd";
import moment from "moment";
import type { Moment } from "moment";
import { useAppSelector } from "../../..";
import { validateOfferNameUniqueness } from "../../../containers/OfferContainer/utils/validateOfferNameUniqueness";
import { Agreement, ITenant, Offer } from "../../../lib/types";
import { IRuleCondition } from "../../../lib/types/IRuleCondition";
import { round } from "../../../lib/utils";
import { IOfferListSearch } from "../../../store/slices/offering/types";
import { ProductOfferParamsEdit } from "./ProductOfferParamsEdit/ProductOfferParamsEdit";
import { ProductServiceProfileEdit } from "./ProductServiceProfileEdit/ProductServiceProfileEdit";
import { hasAccess } from "../../RolesModule/hasAccess";
import { PROLONGATION_WRITE } from "../../../lib/const/permissions";
import { IOfferParamsTableRecord } from "../../../containers/AccountProductsContainer/ProductCreateContainer/utils/types";
import { ReactComponent as PlusIcon } from "../../../img/plus.svg";
import {
  IOfferTarifficationFormData,
  IOfferParamsFormData,
  IOfferAggRuleFormData,
  IOfferListSelectMode,
  IOfferServiceProfileFormData,
} from "../../../containers/OfferContainer/context/types";
import { CharacteristicsContainer } from "../../../modules/policies";
import { IViewModeExtended } from "../../../lib/types/IViewMode";
import { useCharacteristicsInitialisation } from "../../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import { ServiceRateEdit } from "../../common/ServiceRate/ServiceRateEdit/ServiceRateEdit";
import { FormInfo } from "../../common/FormInfo/FormInfo";
import { AggregationRulesEdit } from "../../../modules/AggregationRules";
import { AggregationRule } from "../../../lib/types/AggregationRule";
import { onAddPrice } from "../../../containers/OfferContainer/utils/onAddPrice";
import { onChangeTariffModelId } from "../../../containers/OfferContainer/utils/onChangeTariffModelId";
import "./ProductOfferDetailsModal.less";

interface ComponentProps {
  visible: boolean;
  tableRecordKey: string;
  offer: Offer<Moment, "createForm"> | undefined;
  searchListValues: IOfferListSearch;
  closeOfferDetailsModal: () => void;
  onChangeTableOfferData: (props: { fieldName: keyof Offer; key: string; value: any }) => void;
  onChangeTableData: (props: {
    fieldName: keyof IOfferParamsTableRecord;
    key: string;
    value: any;
    changeMode?: "single" | "children" | "all" | undefined;
  }) => void;
  newRuleCondition: Partial<IRuleCondition>;
  getNewAggregationRule: () => AggregationRule<"createForm">;
  offerListSelectMode: IOfferListSelectMode;
  selectedAgreement?: Agreement;
  setIsCreatedOfferValid: React.Dispatch<React.SetStateAction<boolean>>;
  isRoot?: boolean;
}

export const ProductOfferDetailsModal = (props: ComponentProps) => {
  const {
    visible,
    offer,
    searchListValues,
    closeOfferDetailsModal,
    onChangeTableOfferData,
    onChangeTableData,
    tableRecordKey,
    newRuleCondition,
    getNewAggregationRule,
    offerListSelectMode,
    selectedAgreement,
    setIsCreatedOfferValid,
    isRoot,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const { offeringStatuses } = useAppSelector((state) => state.dicts);
  const { tax } = useAppSelector((state) => state.taxTs);

  // Список групп трафика для использования в форме тарификации. Нужен, т.к. в antd 4 отсуствует возможность получения значения в моменте
  const [aggregationRuleList, setAggregationRuleList] = useState<AggregationRule<"createForm">[]>();

  const [offerParamsForm] = Form.useForm<IOfferParamsFormData>();
  const [aggregationRulesForm] = Form.useForm<IOfferAggRuleFormData>();
  const [tarifficationForm] = Form.useForm<IOfferTarifficationFormData>();
  const [serviceProfileForm] = Form.useForm<IOfferServiceProfileFormData>();

  const tenant = useAppSelector((state) => state.tenants.tenant) as ITenant;

  // В России редактируется цена без налога, иначе - с. Флаг об этом
  // const isEditPriceWithVat = useMemo(() => (tenantProperties as any)?.inputTax === "true", [
  //   tenantProperties,
  // ]);

  // Сохранение изменений в данных предложения
  const saveProductChanges = ({ name, extName }: IOfferParamsFormData) => {
    onChangeTableData({ fieldName: "name", key: tableRecordKey, value: name });
    onChangeTableData({ fieldName: "extName", key: tableRecordKey, value: extName });
  };

  // Параметры оффера из формы сохранятся в tableData после сабмита формы
  const saveOfferParamsChanges = (values: IOfferParamsFormData) => {
    Object.keys(values).forEach((name) => {
      const fieldName = name as keyof Offer;
      const value = values[fieldName] as Offer[keyof Offer];
      onChangeTableOfferData({
        key: tableRecordKey,
        fieldName,
        value,
      });
    });
  };

  // Изначально берется из входящего оффера, по изменении в таблице, меняется
  const [isPacketTariff, setIsPacketTariff] = useState(false);

  useEffect(() => {
    setIsPacketTariff(offer?.tariffModelId === 2);
  }, [offer]);

  // Группы трафика из формы сохранятся в tableData после сабмита формы
  const aggregationRulesChanges = (values: IOfferAggRuleFormData) => {
    onChangeTableOfferData({
      key: tableRecordKey,
      fieldName: "aggregationRules",
      value: values.aggregationRules,
    });
  };

  // Параметры тарификации из формы сохранятся в tableData после сабмита формы
  const saveTarifficationChanges = (values: IOfferTarifficationFormData) => {
    onChangeTableOfferData({
      key: tableRecordKey,
      fieldName: "serviceRates",
      value: values.serviceRates,
    });
  };

  // Профиль оплаты из формы сохранятся в tableData после сабмита формы
  const saveServiceProfileChanges = (values: IOfferServiceProfileFormData) => {
    onChangeTableOfferData({
      key: tableRecordKey,
      fieldName: "serviceProfile",
      value: values,
    });
  };

  const onCancel = () => {
    offerParamsForm.resetFields();
    aggregationRulesForm.resetFields();
    tarifficationForm.resetFields();
    serviceProfileForm.resetFields();
    closeOfferDetailsModal();
  };

  const onSubmit = () => {
    Promise.all([
      offerParamsForm.validateFields(),
      aggregationRulesForm.validateFields(),
      tarifficationForm.validateFields(),
      serviceProfileForm.validateFields(),
    ])
      .then(([offerParamsData, aggregationRulesData, tarifficationData, serviceProfileData]) => {
        setIsCreatedOfferValid(true);
        saveProductChanges(offerParamsData);
        saveOfferParamsChanges(offerParamsData);
        aggregationRulesChanges(aggregationRulesData);
        saveTarifficationChanges(tarifficationData);
        saveServiceProfileChanges(serviceProfileData);
        offerParamsForm.resetFields();
        aggregationRulesForm.resetFields();
        tarifficationForm.resetFields();
        serviceProfileForm.resetFields();
        closeOfferDetailsModal();
        setIsLoading(false);
      })
      .catch(() => {
        setIsCreatedOfferValid(false);
        setIsLoading(false);
      });
  };

  // Исходные данные параметров предложения
  useEffect(() => {
    const initialValues: Partial<IOfferParamsFormData> = {
      extName: offer?.extName,
      name: offer?.name,
      serviceTypeId: offer?.serviceTypeId,
      offeringTypeId: offerListSelectMode === "select" ? offer?.offeringTypeId : 3,
      agreementId: selectedAgreement?.id,
      prolongTypeId: offer?.prolongTypeId,
      lifespanPeriodId: offer?.lifespanPeriodId,
      lifespanPeriodQty: offer?.lifespanPeriodQty,
      isStandalone: isRoot ? true : offer?.isStandalone,
      offeringStatusId: offer?.offeringStatusId,
      bundleTypeId:
        offerListSelectMode === "basedOn" || offerListSelectMode === "select"
          ? offer?.bundleTypeId
          : 1,
      fromDate: offer?.fromDate,
      toDate: offer?.toDate,
      periodAlignmentId: offer?.periodAlignmentId,
      tariffModelId: offer?.tariffModelId,
    };

    offerParamsForm.setFieldsValue(initialValues);
  }, [offer, selectedAgreement, offerListSelectMode, isRoot, offerParamsForm]);

  // Исходные данные групп трафика
  useEffect(() => {
    if (offer?.aggregationRules) {
      aggregationRulesForm.setFieldsValue({ aggregationRules: offer.aggregationRules });
      setAggregationRuleList(offer.aggregationRules);
    }
  }, [aggregationRulesForm, offer]);

  const [tarifficationCollapseKeys, setTarifficationCollapseKeys] = useState<string | string[]>([]);

  // Исходные данные цены
  useEffect(() => {
    if (offer?.serviceRates) {
      if (
        offerListSelectMode === "basedOn" ||
        offerListSelectMode === "select" ||
        offerListSelectMode === "new"
      ) {
        const taxRate = tax?.taxRate || "0";
        const taxMultiplier = Number(taxRate) / 100 + 1;

        const initialServiceRates = offer?.serviceRates?.map((item) => {
          const { price } = item;
          const priceWithVat = round(Number(item.price) * taxMultiplier);

          return {
            ...item,
            price,
            priceWithVat,
            fromDate: moment(item.fromDate),
            toDate: moment(item.toDate),
            balanceTypeId: 1,
          };
        });
        if (initialServiceRates.length) {
          setTarifficationCollapseKeys(["1"]);
        }

        tarifficationForm.setFieldsValue({ serviceRates: initialServiceRates });
      }
    } else {
      tarifficationForm.setFieldsValue({ serviceRates: undefined });
    }
  }, [offerListSelectMode, offer, selectedAgreement, tax, tarifficationForm]);

  // Исходные данные профиля оплаты
  useEffect(() => {
    const initialValues: Partial<IOfferServiceProfileFormData> =
      // offerListSelectMode === "basedOn" || offerListSelectMode === "select"
      //   ?
      {
        ...offer?.serviceProfile,
        fromDate: offer?.serviceProfile?.fromDate
          ? moment(offer.serviceProfile.fromDate)
          : undefined,
        toDate: offer?.serviceProfile?.toDate ? moment(offer.serviceProfile.toDate) : undefined,
        profileTypeId: offerListSelectMode === "select" ? offer?.serviceProfile.profileTypeId : 2,
      };
    // : { profileTypeId: defaultValues.profileTypeId };
    serviceProfileForm.setFieldsValue(initialValues);
  }, [offer, offerListSelectMode, serviceProfileForm]);

  // Очистка данных профиля оплаты (нужно при переключении типа предложения: общий/индивидуальный)
  const clearServiceProfile = () => {
    serviceProfileForm.setFieldsValue({
      id: undefined,
      paymentTypeId: undefined,
      prepaymentToPayDays: undefined,
      invoicePeriodId: undefined,
      billToPayDays: undefined,
      prepaymentPeriodId: undefined,
      billPeriodId: undefined,
    });
  };

  // Изменение зависимых полей, по изменении значений в параметрах предложения
  // TODO - доработать, т.к. изменение должно фиксироваться только по сабмиту модалки (см. saveProductChanges)
  const onChangeOfferParams = (data: Partial<IOfferParamsFormData>) => {
    const fieldName = Object.keys(data)[0] as keyof IOfferParamsFormData;
    const value = Object.values(data)[0] as any;
    switch (fieldName) {
      case "offeringTypeId":
        break;
      case "extName":
        break;
      case "prolongTypeId":
        if (value === 1 && !hasAccess(PROLONGATION_WRITE)) {
          onChangeTableData({
            key: tableRecordKey,
            fieldName: "prolongTypeId",
            value: undefined,
          });
          onChangeTableData({
            key: tableRecordKey,
            fieldName: "prolongOffer",
            value: undefined,
          });
        }
        break;
      default:
        break;
    }
  };

  const handleAddPrice = useCallback(
    (e: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
      e.stopPropagation();
      setTarifficationCollapseKeys(["1"]);
      onAddPrice({
        form: tarifficationForm,
        taxRate: round(tax?.taxRate || 0),
        currencyId: tenant?.defaultCurrencyId,
        isPacketTariff,
      });
    },
    [isPacketTariff, tarifficationForm, tax, tenant]
  );

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    fieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    productCharValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
    setProductCharValuesCharacteristic,
  } = useCharacteristicsInitialisation({
    initialFromDate: offer?.fromDate,
  });
  // ---> Характеристики <---

  const getCharacteristicView = (offer, mode: IViewModeExtended, isChild?: boolean) => {
    return (
      <div className={`${isChild ? "row_child_char row_child" : ""}`}>
        <CharacteristicsContainer
          objectTypeId={1043}
          mode={mode}
          title="Характеристики Предложения"
          tableData={infoCharacteristic}
          setTableData={setInfoCharacteristic}
          dataSaved={offer?.info}
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          fieldsError={fieldsError}
        />
        <CharacteristicsContainer
          mode={mode}
          title="Параметры доступности для Клиента"
          isInheritableObject
          isSelectedObjectId
          tableData={charValuesCharacteristic}
          setTableData={setCharValuesCharacteristic}
          dataSaved={offer?.charValues}
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          isDefaultDisabled
          disabledValidation
          fieldsError={fieldsError}
        />
        <CharacteristicsContainer
          objectTypeId={1032}
          mode={mode}
          title="Параметры Предложения (характеристики Продукта)"
          isInheritableObject
          tableData={productCharValuesCharacteristic}
          setTableData={setProductCharValuesCharacteristic}
          dataSaved={offer?.productCharValues}
          valuesTitle="Допустимые значения"
          fromDateCharacteristic={fromDateCharacteristic}
          messageWarningNotFromDate="Необходимо выбрать Дату создания предложения"
          disabledValidation
          fieldsError={fieldsError}
        />
      </div>
    );
  };

  const [updateAggRuleCollapseState, setUpdateAggRuleCollapseState] = useState(false);

  useEffect(() => {
    setUpdateAggRuleCollapseState((prev) => !prev);
  }, [offer, visible]);

  return (
    <Modal
      className="productOffer-details-modal"
      visible={visible}
      onCancel={onCancel}
      width="100%"
      okButtonProps={{
        style: {
          width: "108px",
        },
        onClick: () => {
          if (offerListSelectMode === "select") {
            onCancel();
          } else {
            setIsLoading(true);
            setTimeout(onSubmit, 0);
          }
        },
        loading: isLoading,
      }}
      cancelButtonProps={{
        style: {
          width: "108px",
        },
        onClick: onCancel,
      }}
    >
      <Spin spinning={isLoading} className="productOffer-details-modal__spinner">
        <Collapse bordered={false} defaultActiveKey="1">
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="productOffer-details-modal__pageTitle">
                <h3>Параметры предложения</h3>
              </Row>
            }
          >
            <Form form={offerParamsForm} onValuesChange={onChangeOfferParams}>
              <ProductOfferParamsEdit
                offer={offer}
                isRoot={isRoot}
                viewMode="create"
                allowedOfferingStatuses={offeringStatuses}
                onNameBlur={(name) =>
                  validateOfferNameUniqueness({
                    name,
                    viewMode: "create",
                  })
                }
                form={offerParamsForm}
                searchListValues={searchListValues}
                disabledFields={["fromDate", "toDate"]}
                offerListSelectMode={offerListSelectMode}
                selectedAgreement={selectedAgreement}
                onChangeTariffModelId={(tariffModelTypeId) =>
                  onChangeTariffModelId({
                    tariffModelTypeId,
                    offerParamsForm,
                    setIsPacketTariff,
                    tarifficationForm,
                  })
                }
              />
            </Form>
            {getCharacteristicView(offer, "view", false)}
          </Collapse.Panel>
        </Collapse>
        <AggregationRulesEdit
          // className="offer-create-container__aggregationRules"
          aggregationRulesForm={aggregationRulesForm}
          availableFields={["aggName", "periodTypeId", "rangeFrom", "rangeTo", "ruleCondition"]}
          requiredFields={["aggName", "rangeFrom", "periodTypeId"]}
          getNewAggregationRule={getNewAggregationRule}
          newRuleCondition={newRuleCondition}
          setAggregationRuleList={(rules) => {
            setAggregationRuleList(rules);
          }}
          hidePeriod
          offerListSelectMode={offerListSelectMode}
          updateCollapse={updateAggRuleCollapseState}
        />
        <Collapse
          bordered={false}
          activeKey={tarifficationCollapseKeys}
          onChange={(keys) => {
            setTarifficationCollapseKeys(keys);
          }}
        >
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="offer-create-container__pageTitle">
                <h3>Тарификация</h3>
                {offerListSelectMode !== "select" && (
                  <PlusIcon onClick={handleAddPrice} className="serviceRate-edit__addButton" />
                )}
              </Row>
            }
          >
            <Form form={tarifficationForm} labelAlign="left">
              <ServiceRateEdit
                form={tarifficationForm}
                isPacketTariff={isPacketTariff}
                offerListSelectMode={offerListSelectMode}
                aggregationRuleList={aggregationRuleList}
                onRemoveLastRate={() => {
                  setTarifficationCollapseKeys([]);
                }}
                windowWidth={1650}
              />
            </Form>
          </Collapse.Panel>
        </Collapse>
        <Collapse bordered={false} defaultActiveKey="1">
          <Collapse.Panel
            key="1"
            header={
              <Row justify="space-between" className="productOffer-details-modal__pageTitle">
                <h3>Профиль оплаты</h3>
              </Row>
            }
          >
            {offer?.serviceProfile && (
              <Form form={serviceProfileForm}>
                <ProductServiceProfileEdit
                  clearServiceProfile={clearServiceProfile}
                  viewMode="create"
                  serviceProfileForm={serviceProfileForm}
                  offer={offer}
                  editingAllowed={offerListSelectMode !== "select"}
                />
              </Form>
            )}
          </Collapse.Panel>
        </Collapse>
        <FormInfo
          getFormValues={() => ({
            offerParamsForm: offerParamsForm.getFieldsValue(),
            aggregationRulesForm: aggregationRulesForm.getFieldsValue(),
            tarifficationForm: tarifficationForm.getFieldsValue(),
            serviceProfileForm: serviceProfileForm.getFieldsValue(),
          })}
        />
      </Spin>
    </Modal>
  );
};
